/**
 * Power Apps REST API client
 * Covers both Maker and Admin API surfaces
 */

import { requestWithRetry } from "../utils/http.js";
import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import type {
  PowerApp,
  PowerAppVersion,
  PowerAppRoleAssignment,
  ApiListResponse,
} from "./types.js";
import { createChildLogger } from "../utils/logger.js";
import {
  ApiError,
  AuthorizationError,
  RateLimitError,
  wrapApiCall,
} from "../utils/errors.js";
import { POWERAPPS_API_BASE, POWERAPPS_API_VERSION } from "../utils/constants.js";
import {
  validateRowId,
  validateEnvironmentId,
  sanitizeErrorMessage,
  escapeOData,
} from "../utils/security.js";

const apiLogger = createChildLogger({ module: "powerapps-api" });

export interface PowerAppsApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

/**
 * Client for Power Apps REST API (Maker + Admin)
 */
export class PowerAppsApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: PowerAppsApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = config.defaultEnvironmentId;
  }

  private async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWERAPPS_SERVICE);
    return {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };
  }

  private buildUrl(
    path: string,
    queryParams?: Record<string, string | number | undefined>
  ): string {
    const base = `${POWERAPPS_API_BASE}${path}`;
    const params = new URLSearchParams();
    params.set("api-version", POWERAPPS_API_VERSION);

    if (queryParams) {
      for (const [key, value] of Object.entries(queryParams)) {
        if (value !== undefined) {
          params.set(key, String(value));
        }
      }
    }

    return `${base}?${params.toString()}`;
  }

  private async handleResponse<T>(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string
  ): Promise<T> {
    const { statusCode, body } = response;

    if (statusCode === 429) {
      try {
        await body.text();
      } catch {
        body.destroy();
      }
      const retryAfter = response.headers["retry-after"];
      let retryMs: number | undefined;
      if (retryAfter) {
        const seconds = parseInt(String(retryAfter), 10);
        if (!Number.isNaN(seconds)) {
          retryMs = seconds * 1000;
        } else {
          const date = new Date(String(retryAfter));
          if (!Number.isNaN(date.getTime())) {
            retryMs = Math.max(0, date.getTime() - Date.now());
          }
        }
      }
      throw new RateLimitError(retryMs);
    }

    // Stream-read body with size limit to prevent OOM
    const MAX_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB
    const chunks: Buffer[] = [];
    let totalSize = 0;
    for await (const chunk of body as AsyncIterable<Uint8Array>) {
      totalSize += chunk.length;
      if (totalSize > MAX_RESPONSE_SIZE) {
        body.destroy();
        throw new ApiError(`${context}: response too large (max 50MB)`, 413);
      }
      chunks.push(Buffer.from(chunk));
    }
    const responseText = Buffer.concat(chunks).toString("utf-8");

    if (statusCode === 401) {
      throw new AuthorizationError(`${context}: Authentication failed`);
    }

    if (statusCode === 403) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      apiLogger.error({ statusCode, errorBody: sanitizedBody, context }, "Access denied");
      throw new AuthorizationError(
        `${context}: Requires Power Platform admin role or app ownership`
      );
    }

    if (statusCode === 404) {
      const sanitizedBody = sanitizeErrorMessage(responseText);
      throw new ApiError(`${context}: Not found - ${sanitizedBody}`, 404);
    }

    if (statusCode >= 400) {
      let errorDetails: unknown;
      try {
        errorDetails = JSON.parse(responseText);
      } catch {
        errorDetails = sanitizeErrorMessage(responseText);
      }
      throw new ApiError(`${context}: API error`, statusCode, errorDetails);
    }

    if (!responseText) {
      if (statusCode === 204 || statusCode === 202) {
        return {} as T;
      }
      throw new ApiError(
        `${context}: Server returned empty response (status ${statusCode})`,
        statusCode
      );
    }

    try {
      return JSON.parse(responseText) as T;
    } catch (e) {
      throw new ApiError(
        `${context}: Invalid JSON response (status ${statusCode})`,
        statusCode,
        { parseError: String(e), responsePreview: sanitizeErrorMessage(responseText.slice(0, 200)) }
      );
    }
  }

  // ==========================================================================
  // Maker APIs — /providers/Microsoft.PowerApps/apps
  // ==========================================================================

  async listApps(environment?: string): Promise<PowerApp[]> {
    const envId = validateEnvironmentId(environment ?? this.defaultEnvironmentId);
    apiLogger.debug({ envId }, "Listing Power Apps");

    const url = this.buildUrl("/providers/Microsoft.PowerApps/apps", {
      $filter: `environment eq '${escapeOData(envId)}'`,
      $top: 250,
    });
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<PowerApp>>(response, "List apps");
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ data, context: "List apps" }, "Unexpected response shape");
        return [];
      }
      apiLogger.info({ count: data.value.length }, "Listed Power Apps");
      return data.value;
    }, "List Power Apps");
  }

  async getApp(appId: string): Promise<PowerApp> {
    const safeAppId = validateRowId(appId);
    apiLogger.debug({ appId: safeAppId }, "Getting Power App");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apps/${safeAppId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<PowerApp>(response, `Get app ${safeAppId}`);
    }, `Get app ${safeAppId}`);
  }

  async deleteApp(appId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    apiLogger.info({ appId: safeAppId }, "Deleting Power App");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apps/${safeAppId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Delete app ${safeAppId}`);
      apiLogger.info({ appId: safeAppId }, "App deleted");
    }, `Delete app ${safeAppId}`);
  }

  async publishApp(appId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    apiLogger.info({ appId: safeAppId }, "Publishing Power App");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apps/${safeAppId}/publish`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers, body: "{}" });
      await this.handleResponse<unknown>(response, `Publish app ${safeAppId}`);
      apiLogger.info({ appId: safeAppId }, "App published");
    }, `Publish app ${safeAppId}`);
  }

  async getAppVersions(appId: string): Promise<PowerAppVersion[]> {
    const safeAppId = validateRowId(appId);
    apiLogger.debug({ appId: safeAppId }, "Getting app versions");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apps/${safeAppId}/versions`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<PowerAppVersion>>(
        response,
        `Get versions ${safeAppId}`
      );
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: `Get versions ${safeAppId}`, responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, `Get versions ${safeAppId}`);
  }

  async restoreAppVersion(appId: string, versionId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeVersionId = validateRowId(versionId);
    apiLogger.info({ appId: safeAppId, versionId: safeVersionId }, "Restoring app version");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/apps/${safeAppId}/versions/${safeVersionId}/restore`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "POST", headers, body: "{}" });
      await this.handleResponse<unknown>(response, `Restore version ${safeVersionId}`);
    }, `Restore version ${safeVersionId}`);
  }

  async setAppDisplayName(appId: string, displayName: string): Promise<PowerApp> {
    const safeAppId = validateRowId(appId);
    apiLogger.info({ appId: safeAppId, displayName }, "Setting app display name");

    const url = this.buildUrl(`/providers/Microsoft.PowerApps/apps/${safeAppId}`);
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "PATCH",
        headers,
        body: JSON.stringify({ properties: { displayName } }),
      });
      return this.handleResponse<PowerApp>(response, `Update app ${safeAppId}`);
    }, `Update app ${safeAppId}`);
  }

  async getAppPermissions(appId: string): Promise<PowerAppRoleAssignment[]> {
    const safeAppId = validateRowId(appId);
    apiLogger.debug({ appId: safeAppId }, "Getting app permissions");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/apps/${safeAppId}/roleAssignments`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<PowerAppRoleAssignment>>(
        response,
        `Get permissions ${safeAppId}`
      );
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: `Get permissions ${safeAppId}`, responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, `Get permissions ${safeAppId}`);
  }

  async shareApp(
    appId: string,
    principalId: string,
    principalType: "User" | "Group" | "Tenant" | "ServicePrincipal",
    roleName: "CanEdit" | "CanView",
    tenantId?: string
  ): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safePrincipalId = validateRowId(principalId);
    apiLogger.info({ appId: safeAppId, principalId: safePrincipalId, roleName }, "Sharing Power App");

    const assignmentId = `${safeAppId}/roleAssignments/${safePrincipalId}`;
    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/apps/${assignmentId}`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          properties: {
            roleName,
            principal: { id: safePrincipalId, type: principalType, tenantId },
            notifyShareTargetOption: "Notify",
          },
        }),
      });
      await this.handleResponse<unknown>(response, `Share app ${safeAppId}`);
    }, `Share app ${safeAppId}`);
  }

  async unshareApp(appId: string, roleAssignmentId: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeRoleAssignmentId = validateRowId(roleAssignmentId);
    apiLogger.info({ appId: safeAppId, roleAssignmentId: safeRoleAssignmentId }, "Unsharing Power App");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/apps/${safeAppId}/roleAssignments/${safeRoleAssignmentId}`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Unshare app ${safeAppId}`);
    }, `Unshare app ${safeAppId}`);
  }

  // ==========================================================================
  // Admin APIs — /providers/Microsoft.PowerApps/scopes/admin
  // ==========================================================================

  async listAppsAdmin(environment?: string): Promise<PowerApp[]> {
    // Every sibling defaults to the configured environment; demanding a GUID
    // here made the tool fail on its first call.
    const safeEnv = validateEnvironmentId(environment ?? this.defaultEnvironmentId);
    apiLogger.debug({ environment: safeEnv }, "Listing apps (admin)");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/scopes/admin/environments/${safeEnv}/apps`,
      { $top: 250 }
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      const data = await this.handleResponse<ApiListResponse<PowerApp>>(
        response,
        "List apps (admin)"
      );
      if (!data?.value || !Array.isArray(data.value)) {
        apiLogger.warn({ context: "List apps (admin)", responseKeys: data ? Object.keys(data) : "null" }, "Unexpected response shape");
        return [];
      }
      return data.value;
    }, "List apps (admin)");
  }

  async getAppAdmin(appId: string, environment: string): Promise<PowerApp> {
    const safeAppId = validateRowId(appId);
    const safeEnv = validateEnvironmentId(environment);
    apiLogger.debug({ appId: safeAppId, environment: safeEnv }, "Getting app (admin)");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/scopes/admin/environments/${safeEnv}/apps/${safeAppId}`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "GET", headers });
      return this.handleResponse<PowerApp>(response, `Get app admin ${safeAppId}`);
    }, `Get app admin ${safeAppId}`);
  }

  async deleteAppAdmin(appId: string, environment: string): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeEnv = validateEnvironmentId(environment);
    apiLogger.info({ appId: safeAppId, environment: safeEnv }, "Deleting app (admin)");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/scopes/admin/environments/${safeEnv}/apps/${safeAppId}`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method: "DELETE", headers });
      await this.handleResponse<unknown>(response, `Delete app admin ${safeAppId}`);
    }, `Delete app admin ${safeAppId}`);
  }

  async setAppOwner(
    appId: string,
    environment: string,
    newOwnerId: string,
    newOwnerType: "User" | "ServicePrincipal" = "User"
  ): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeEnv = validateEnvironmentId(environment);
    const safeOwnerId = validateRowId(newOwnerId);
    apiLogger.warn({ appId: safeAppId, environment: safeEnv, newOwnerId: safeOwnerId }, "Changing app owner");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/scopes/admin/environments/${safeEnv}/apps/${safeAppId}/modifyAppOwner`
    );
    const headers = await this.getAuthHeaders();

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, {
        method: "POST",
        headers,
        body: JSON.stringify({
          newAppOwner: safeOwnerId,
          newAppOwnerType: newOwnerType,
        }),
      });
      await this.handleResponse<unknown>(response, `Set owner ${safeAppId}`);
    }, `Set owner ${safeAppId}`);
  }

  async quarantineApp(
    appId: string,
    environment: string,
    quarantine: boolean
  ): Promise<void> {
    const safeAppId = validateRowId(appId);
    const safeEnv = validateEnvironmentId(environment);
    apiLogger.info({ appId: safeAppId, environment: safeEnv, quarantine }, "Quarantining app");

    const url = this.buildUrl(
      `/providers/Microsoft.PowerApps/scopes/admin/environments/${safeEnv}/apps/${safeAppId}/quarantine`
    );
    const headers = await this.getAuthHeaders();
    const method = quarantine ? "PUT" : "DELETE";

    return wrapApiCall(async () => {
      const response = await requestWithRetry(url, { method, headers });
      await this.handleResponse<unknown>(
        response,
        `${quarantine ? "Quarantine" : "Unquarantine"} app ${safeAppId}`
      );
    }, `Quarantine app ${safeAppId}`);
  }
}
